/**
 * Source publication lag ("data freshness").
 *
 * Vendors publish yesterday's numbers, not this second's: GitHub won't report
 * a day that hasn't finished, AWS Cost Explorer needs a day to settle,
 * Anthropic's analytics run ~a day behind. Connectors declare this per stream
 * as `data_delay_days` (see `connector_sdk.base.Stream`) and clamp their
 * extract window to it; it reaches the UI on the catalog spec's streams.
 *
 * It has to be on screen, because invisible it looks exactly like a bug: a
 * green sync at 09:00 today whose newest row is from yesterday, and — the
 * case that sent us looking — a month-to-date figure sitting at $0 for the
 * first days of a month because every day in the window is still unpublished
 * at the source.
 *
 * Shared by the Data Sources page (Configuration card + Sync schedule step)
 * and the Sync History backfill drawer.
 */

/**
 * Newest day a sync can load, given a lag in days.
 *
 * UTC because that is the clock the connectors and the vendors' day
 * boundaries use; a local rendering would read a day off for anyone east of
 * Greenwich.
 */
export function freshestLoadableDay(days) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}

function lagLabel(min, max) {
  if (min !== max) {return `${min}–${max} days`;}
  return `${max} day${max === 1 ? '' : 's'}`;
}

/**
 * Reduce a connector's per-stream lags to one summary, narrowed to the
 * streams a connection actually syncs.
 *
 * Returns null when no stream declares a lag — say nothing rather than imply
 * same-day freshness we haven't verified.
 *
 * @param spec            catalog connector spec (needs `.streams`)
 * @param selectedStreams stream names to consider; falsy/empty = all
 */
export function sourceLagSummary(spec, selectedStreams) {
  const all = spec?.streams || [];
  const picked = selectedStreams?.length
    ? all.filter((s) => selectedStreams.includes(s.name))
    : all;
  const declared = picked
    .map((s) => s.data_delay_days)
    .filter((d) => typeof d === 'number' && d >= 0);
  if (!declared.length) {return null;}
  const min = Math.min(...declared);
  const max = Math.max(...declared);
  return {
    min,
    max,
    mixed: min !== max,
    label: lagLabel(min, max),
    // The conservative end: with a mixed connector, this is the day by which
    // EVERY selected table is loadable.
    freshestDay: freshestLoadableDay(max),
  };
}

export function sourceLagExplanation(spec, lag) {
  const vendor = spec?.display_name || spec?.name || 'This source';
  return (
    `${vendor} publishes its data with a delay of ${lag.label}, so the newest day `
    + `any sync can load is ${lag.freshestDay} (UTC). This is a limit of the source, `
    + `not of the schedule — syncing more often does not make the data fresher, and a `
    + `run whose newest row is a day or more old is working correctly. `
    + `Month-to-date figures therefore stay empty for the first ${lag.label} of each `
    + `month until the source publishes them.`
  );
}
